import { format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { calculateShiftDuration, calculateShiftDurationInMinutes, formatHoursAndMinutes } from './dateUtils';

// Identify sick leave titles
const SICK_LEAVE_TITLES = [
  "ΑΝΑΡΡΩΤΙΚΗ", "ΑΝΑΡΡΩΤΙΚΕΣ", "ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ", "SICK LEAVE", "SICK"
];

// Check if a schedule is a sick leave
export const isSickLeave = (title: string): boolean => {
  if (!title) return false;
  
  const normalizedTitle = title.toUpperCase().trim();
  return SICK_LEAVE_TITLES.some(keyword => 
    normalizedTitle === keyword || normalizedTitle.includes(keyword)
  );
};

// Calculate hours for a guard across all schedules (now working in minutes for precision)
export const calculateGuardHours = (
  teamId: string,
  teamName: string,
  schedules: any[],
  guardProfilesMap: Map<any, any>,
  guardTeamMinutesMap: Map<string, Map<string, number>>,
  guardTotalMinutesMap: Map<string, number>,
  includeLeaveHours: boolean = true
) => {
  // Initialize a map to track sick leave minutes
  const guardSickLeaveMinutesMap = new Map<string, number>();

  // For each schedule in this team
  for (const schedule of schedules) {
    const guardIds = schedule.assigned_guards || [];
    
    // Skip excluded shifts (marked with (EXC) in title or training sessions)
    if (schedule.title && schedule.title.includes('(EXC)')) {
      console.log(`[HOURS DEBUG] Skipping excluded schedule: ${schedule.title} (marked with EXC)`);
      continue;
    }
    
    // Also skip training sessions by description (for backwards compatibility)
    if (schedule.description && schedule.description.startsWith('TRAINING_SESSION:')) {
      console.log(`[HOURS DEBUG] Skipping training session: ${schedule.title}`);
      continue;
    }
    
    // Skip schedules with leave types we want to exclude
    // Only skip if we're not including leave hours (for red/purple alerts)
    if (!includeLeaveHours && shouldExcludeSchedule(schedule.title)) {
      console.log(`[HOURS DEBUG] Skipping excluded schedule: ${schedule.title} (includeLeaveHours=${includeLeaveHours})`);
      continue;
    }
    
    // Check if this is a sick leave schedule
    const isSickLeaveSchedule = isSickLeave(schedule.title);
    
    // Calculate duration in minutes for this schedule
    const startDate = fromLocalISOString(schedule.start_date);
    const endDate = fromLocalISOString(schedule.end_date);
    
    // Use exact duration in minutes
    const durationMinutes = calculateShiftDurationInMinutes(startDate, endDate);
    
    // Add minutes for each assigned guard
    for (const guardId of guardIds) {
      if (!guardId) continue;
      
      // Initialize team minutes map for this guard if it doesn't exist
      if (!guardTeamMinutesMap.has(guardId)) {
        guardTeamMinutesMap.set(guardId, new Map());
      }
      
      // Initialize team minutes for this guard if they don't exist
      const teamMinutesMap = guardTeamMinutesMap.get(guardId)!;
      if (!teamMinutesMap.has(teamId)) {
        teamMinutesMap.set(teamId, 0);
      }
      
      // Add minutes for this team
      const currentTeamMinutes = teamMinutesMap.get(teamId) || 0;
      teamMinutesMap.set(teamId, currentTeamMinutes + durationMinutes);
      
      // Add to total minutes
      const currentTotalMinutes = guardTotalMinutesMap.get(guardId) || 0;
      guardTotalMinutesMap.set(guardId, currentTotalMinutes + durationMinutes);

      // If this is a sick leave, track the minutes in the sick leave map
      if (isSickLeaveSchedule) {
        const currentSickLeaveMinutes = guardSickLeaveMinutesMap.get(guardId) || 0;
        guardSickLeaveMinutesMap.set(guardId, currentSickLeaveMinutes + durationMinutes);
        
        console.log(`[SICK LEAVE] Counted ${formatHoursAndMinutes(durationMinutes)} of sick leave for guard ${guardId}`);
      }
      
      const guard = guardProfilesMap.get(guardId);
      const guardName = guard?.full_name || `${guard?.first_name || ''} ${guard?.last_name || ''}`.trim();
      
      // Special debugging for ΑΛΕΞΑΝΔΡΟΣ ΚΩΝΣΤΑΝΤΙΝΟΥ
      const isSpecialDebug = guardName.includes("ΚΩΝΣΤΑΝΤΙΝΟΥ") || guardName.includes("KONSTANTINOU");
      
      // Enhanced debugging for hours calculation
      if (isSpecialDebug) {
        console.log(`[SPECIAL DEBUG] ${guardName} - Schedule: ${schedule.title}`);
        console.log(`  Start: ${format(startDate, 'yyyy-MM-dd HH:mm')}`);
        console.log(`  End: ${format(endDate, 'yyyy-MM-dd HH:mm')}`);
        console.log(`  Duration: ${formatHoursAndMinutes(durationMinutes)}`);
        console.log(`  Is Sick Leave: ${isSickLeaveSchedule}`);
        console.log(`  Is Leave: ${shouldExcludeSchedule(schedule.title)}`);
        console.log(`  Include Leave Hours: ${includeLeaveHours}`);
        console.log(`  Team Total: ${formatHoursAndMinutes(currentTeamMinutes + durationMinutes)}`);
        console.log(`  Overall Total: ${formatHoursAndMinutes(currentTotalMinutes + durationMinutes)}`);
      } else {
        console.log(`[HOURS DEBUG] ${guardName} - Schedule: ${schedule.title}`);
        console.log(`  Start: ${format(startDate, 'yyyy-MM-dd HH:mm')}`);
        console.log(`  End: ${format(endDate, 'yyyy-MM-dd HH:mm')}`);
        console.log(`  Duration: ${formatHoursAndMinutes(durationMinutes)}`);
        console.log(`  Is Sick Leave: ${isSickLeaveSchedule}`);
        console.log(`  Team Total: ${formatHoursAndMinutes(currentTeamMinutes + durationMinutes)}`);
        console.log(`  Overall Total: ${formatHoursAndMinutes(currentTotalMinutes + durationMinutes)}`);
      }
    }
  }

  // Attach the sick leave minutes map to the function's context so it can be accessed by other functions
  // @ts-ignore
  calculateGuardHours.sickLeaveMinutesMap = guardSickLeaveMinutesMap;
};

// Get sick leave minutes for a guard
export const getSickLeaveMinutes = (guardId: string): number => {
  // @ts-ignore
  const sickLeaveMinutesMap = calculateGuardHours.sickLeaveMinutesMap as Map<string, number> | undefined;
  return sickLeaveMinutesMap?.get(guardId) || 0;
};

// Check if a guard has sufficient total hours (subtract sick leave hours from the balance)
export const hasSufficientTotalHours = (
  guardId: string,
  guardTotalMinutesMap: Map<string, number>
): boolean => {
  const totalMinutes = guardTotalMinutesMap.get(guardId) || 0;
  const sickLeaveMinutes = getSickLeaveMinutes(guardId);
  const adjustedMinutes = totalMinutes - sickLeaveMinutes;
  const adjustedHours = adjustedMinutes / 60;
  
  console.log(`[HOURS CHECK] Guard ${guardId}: Total=${formatHoursAndMinutes(totalMinutes)}, SickLeave=${formatHoursAndMinutes(sickLeaveMinutes)}, Adjusted=${formatHoursAndMinutes(adjustedMinutes)}`);
  
  return adjustedHours >= 38;
};

// Check if guard is one of the special cases we should always skip in alerts
export const isSpecialCaseGuard = (guardName: string): boolean => {
  if (!guardName) return false;
  
  const specialCases = [
    "Κατάστημα", "Apokoronas Store", "Κεντρικά", "Offices",
    "STAFF", "STAFF2", "ΑΠΟΘΗΚΗ", "WAREHOUSE",
    "Test", "Test User", "demo", "Demo Guard"
  ];
  
  return specialCases.some(name => 
    guardName.toLowerCase().includes(name.toLowerCase())
  );
};

// Generate alert content for insufficient hours
export const generateAlertContent = (
  guardName: string,
  teamName: string,
  totalMinutes: number,
  weekStart: string,
  weekEnd: string,
  otherTeamHours?: Map<string, { name: string; minutes: number }>,
  totalMinutesAllTeams?: number,
  sickLeaveMinutes?: number
): string => {
  let content = `${guardName} is scheduled for only ${formatHoursAndMinutes(totalMinutes)} in team ${teamName} for the week of ${weekStart} - ${weekEnd}`;
  
  if (sickLeaveMinutes && sickLeaveMinutes > 0) {
    content += ` (includes ${formatHoursAndMinutes(sickLeaveMinutes)} of sick leave)`;
  }
  
  if (otherTeamHours && otherTeamHours.size > 0) {
    content += `. Guard has additional hours in other teams: `;
    const otherTeamsArray: string[] = [];
    
    otherTeamHours.forEach(value => {
      otherTeamsArray.push(`${value.name}: ${formatHoursAndMinutes(value.minutes)}`);
    });
    
    content += otherTeamsArray.join(', ');
  }
  
  if (totalMinutesAllTeams !== undefined) {
    content += `. Total hours across all teams: ${formatHoursAndMinutes(totalMinutesAllTeams)}`;
    
    if (sickLeaveMinutes && sickLeaveMinutes > 0) {
      const adjustedTotal = totalMinutesAllTeams - sickLeaveMinutes;
      content += ` (${formatHoursAndMinutes(adjustedTotal)} after subtracting sick leave)`;
    }
  }
  
  return content;
};

// Debug log function for guards
export const logGuardDebug = (
  guardId: string,
  guardName: string,
  guardTotalMinutesMap: Map<string, number>,
  guardTeamMinutesMap: Map<string, Map<string, number>>
) => {
  const totalMinutes = guardTotalMinutesMap.get(guardId) || 0;
  
  console.log(`[DEBUG] ${guardName} - Total hours: ${formatHoursAndMinutes(totalMinutes)}`);
  
  const teamMinutesMap = guardTeamMinutesMap.get(guardId);
  if (teamMinutesMap) {
    console.log(`[DEBUG] ${guardName} - Hours by team:`);
    teamMinutesMap.forEach((minutes, teamId) => {
      console.log(`  - ${teamId}: ${formatHoursAndMinutes(minutes)}`);
    });
  }
};

// Check if a schedule should be excluded based on its title
export const shouldExcludeSchedule = (title: string): boolean => {
  if (!title) return false;
  
  // IMPORTANT: Regular leave and previous year leave are REMOVED from this list
  // so they will be counted as normal shifts now
  const excludeKeywords = [
    "ΡΕΠΟ", "REPO", "ΡΕΠΩ", "ΡΕΠY", "ΡΕΠΧ", "ΡΕΠΟΥ", "ΡΕΠA", "OFF", "DAY OFF", "DAYS OFF", 
    "ΡΕΠΟ ΠΡΟΗΓΟΥΜΕΝΟΥ ΜΗΝΑ", "ΡΕΠΟ ΠΡΟΗΓ.ΜΗΝΑ", "ΡΕΠΟ ΠΡΟΗΓ",
    
    "ΑΝΑΡΡΩΤΙΚΗ", "ΑΝΑΡΡΩΤΙΚΕΣ", "ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ", "SICK LEAVE", "SICK",
    
    // Removed "ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ", "ΚΑΝ ΑΔΕΙΑ", etc. entries since they should be counted as normal shifts
    
    "ΕΚΠΑΙΔΕΥΣΗ", "TRAINING", "ΑΡΓΙΑ", "HOLIDAY", "ΑΠΟΥΣΙΑ", "ABSENCE"
  ];
  
  const normalizedTitle = title.toUpperCase().trim();
  
  for (const keyword of excludeKeywords) {
    if (normalizedTitle === keyword.toUpperCase() || normalizedTitle.includes(keyword.toUpperCase())) {
      return true;
    }
  }
  
  return false;
};
